import { useEffect, useRef } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Alert } from '../ui/alert';
import { SyncPipeline } from '../SyncPipeline';
import { getSyncStatus, syncDomain } from '../../lib/api';
import { useUiStore } from '../../store/ui';
import { SyncValuePreview } from './SyncValuePreview';

/**
 * The first-sync body — triggers a sync for a domain (once), renders the live
 * pipeline + the value preview, and reports when the sync has settled. Keyed only
 * by envId, so it's shared by the onboarding Sync step and the add-domain modal;
 * the caller supplies the surrounding chrome (title/footer) and gates its primary
 * action on `onSettled`.
 */
export function SyncPanel({ envId, onSettled }: { envId: string; onSettled: () => void }) {
  const setActiveSyncEnv = useUiStore((s) => s.setActiveSyncEnv);
  const started = useRef(false);

  // Shared with the pipeline (same query key) — lets us see whether a sync has
  // ALREADY started before we trigger one, so a reload/resume (or an ACTIVE-org
  // connect that already kicked a sync) doesn't re-fire syncDomain and hit the
  // per-domain cooldown ("Couldn't start the sync").
  const status = useQuery({
    queryKey: ['sync-status', envId],
    queryFn: () => getSyncStatus(envId),
    enabled: !!envId,
  });
  const hasStarted =
    !!status.data &&
    (status.data.graph.stage !== 'IDLE' ||
      status.data.integrations.some((i) => i.runStatus != null));

  const trigger = useMutation({
    mutationFn: () => syncDomain(envId),
    onSuccess: () => setActiveSyncEnv(envId),
  });

  const triggerRef = useRef(trigger);
  triggerRef.current = trigger;
  useEffect(() => {
    // Wait until we know the current sync state, then decide ONCE: trigger a fresh
    // sync only if none has started; otherwise just show the running/finished one.
    if (started.current || !envId || !status.data) return;
    started.current = true;
    if (hasStarted) {
      setActiveSyncEnv(envId);
      return;
    }
    triggerRef.current.mutate();
  }, [envId, status.data, hasStarted, setActiveSyncEnv]);

  // Reset the one-shot guard when the domain changes (the modal is reused across domains).
  useEffect(() => {
    started.current = false;
  }, [envId]);

  return (
    <>
      {trigger.isError && (
        <Alert variant="danger" className="mb-3">
          Couldn’t start the sync.{' '}
          <button
            type="button"
            onClick={() => trigger.mutate()}
            className="font-semibold underline underline-offset-2"
          >
            Retry
          </button>
        </Alert>
      )}
      <SyncPipeline envId={envId} active onSettled={onSettled} />
      {/* Teach value during the wait, then reveal the real per-category counts as
          the first recommendations land. */}
      <SyncValuePreview envId={envId} />
    </>
  );
}
